import type { IAskConversation, IAskFeedbackInput, IAskInput, IAskRequest } from '../types';

/**
 * Mock assistant: a new request is queued, the first poll shows it running with its steps, the second poll shows
 * the answer. Deterministic so the page test can wait for the answer text.
 */
const store = new Map<string, { record: IAskRequest; polls: number }>();
const conversations = new Map<string, IAskConversation>();
let counter = 0;

const answerFor = (tenant: string, question: string): string =>
  `**${tenant}** has 300 physical venues across 1 brand.\n\n| brand | venues |\n|---|---|\n| Burger King UK | 300 |\n\n\`\`\`sql\nSELECT b.name_en_gb AS brand, count(*) AS venues\nFROM venues v\nJOIN brands_to_venues bv ON bv.fk_venue_id = v.pk_venue_id\nJOIN brands b ON b.pk_brand_id = bv.fk_brand_id\nWHERE v.tmp_is_price_tier = false\nGROUP BY 1\n\`\`\`\n\nRows with tmp_is_price_tier = true were excluded as price tiers. Question asked: "${question}".`;

export const mockCreateAsk = (input: IAskInput): IAskRequest => {
  counter += 1;
  const record: IAskRequest = {
    id: `3b9e2d1c-4f7a-4c8e-9a1b-${String(counter).padStart(12, '0')}`,
    tenant: input.tenant,
    conversation_id: input.conversationId,
    question: input.question,
    status: 'queued',
    answer: null,
    steps: [],
    usage: null,
    model: null,
    error: null,
    caller_uid: 'user_test_id',
    caller_email: 'staff@weareopenr.com',
    created_at: new Date().toISOString(),
    started_at: null,
    finished_at: null,
    feedback: null,
  };
  store.set(record.id, { record, polls: 0 });
  const existing = conversations.get(input.conversationId);
  conversations.set(input.conversationId, {
    id: input.conversationId,
    tenant: input.tenant,
    title: existing?.title ?? input.question.slice(0, 80),
    owner_uid: 'user_test_id',
    created_at: existing?.created_at ?? record.created_at,
    updated_at: record.created_at,
    turns: (existing?.turns ?? 0) + 1,
  });
  return { ...record };
};

export const mockGetAsk = (id: string): IAskRequest => {
  const entry = store.get(id);
  if (!entry) {
    throw Object.assign(new Error('Not found'), { status: 404 });
  }
  entry.polls += 1;
  const { record } = entry;
  if (/\bfail\b/i.test(record.question)) {
    record.status = 'failed';
    record.error = 'The model declined to answer this question';
    record.steps = [
      {
        index: 0,
        tool: 'sql_read',
        input: { sql: 'DROP TABLE venues', purpose: 'oops' },
        output: 'Rejected: Only SELECT (optionally with a CTE) is allowed',
        ok: false,
        elapsed_ms: 3,
      },
    ];
    record.finished_at = new Date().toISOString();
  } else if (entry.polls === 1) {
    record.status = 'running';
    record.model = 'anthropic:claude-opus-5';
    record.started_at = new Date().toISOString();
    record.steps = [
      {
        index: 0,
        tool: 'describe_table',
        input: { table: 'venues' },
        output:
          'burgerking.venues\npk_venue_id: uuid, not null\nname: jsonb, not null\ntmp_is_price_tier: boolean, not null',
        ok: true,
        elapsed_ms: 120,
      },
      {
        index: 1,
        tool: 'sql_read',
        input: {
          sql: 'SELECT count(*) FROM venues WHERE tmp_is_price_tier = false',
          purpose: 'Count physical venues',
        },
        output: '1 rows\n[{"count":300}]',
        ok: true,
        elapsed_ms: 340,
      },
    ];
  } else if (entry.polls >= 2 && record.status !== 'done') {
    record.status = 'done';
    record.answer = answerFor(record.tenant, record.question);
    record.usage = {
      input_tokens: 9_400,
      output_tokens: 820,
      cache_read_input_tokens: 6_100,
      cache_creation_input_tokens: 2_000,
      model_calls: 3,
    };
    record.finished_at = new Date().toISOString();
  }
  return { ...record, steps: [...record.steps] };
};

export const mockAskFeedback = (id: string, input: IAskFeedbackInput): IAskRequest => {
  const entry = store.get(id);
  if (!entry) {
    throw Object.assign(new Error('Not found'), { status: 404 });
  }
  entry.record.feedback = {
    rating: input.rating,
    comment: input.comment?.trim() || null,
    by: 'staff@weareopenr.com',
    at: new Date().toISOString(),
  };
  return { ...entry.record };
};

export const mockListConversations = (tenant: string): IAskConversation[] =>
  Array.from(conversations.values())
    .filter((item) => item.tenant === tenant)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));

export const mockThread = (id: string): IAskRequest[] =>
  Array.from(store.values())
    .map(({ record }) => ({ ...record, steps: [...record.steps] }))
    .filter((record) => record.conversation_id === id)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));

export const mockDeleteConversation = (id: string): { id: string } => {
  conversations.delete(id);
  Array.from(store.entries())
    .filter(([, { record }]) => record.conversation_id === id)
    .forEach(([key]) => store.delete(key));
  return { id };
};

export const resetMockAsks = (): void => {
  store.clear();
  conversations.clear();
  counter = 0;
};
